import { t } from '../../services/locale';
import type { ProfileHostPorts } from '../../services/profileHost';
import { buildModifyProfilePayload } from './profileIdentityRules';
import { getBranchPenalties, resolveCancelMessage } from './profileCancelMessageRules';
import { isPauseAllowed, showContactStudioAboutMembership } from './profileMembershipRules';
import type { ProfileBooking } from './profileBooking.types';
import type { ProfileIdentity } from './profileAccount.types';
import {
  asRecord,
  asUser,
  type ProfileCloseResult,
  type ProfileRuntimeBranch,
  type ProfileRuntimeUser,
  type RuntimeMembership,
} from './profileRuntime';

const GENERAL_DIALOG = 'dialogs/general-dialog/general-dialog.html';
const WAIVERS = 'dialogs/waivers/waivers.html';

type HostModal = {
  element?: { modal?: () => void };
  close?: Promise<unknown>;
  scope?: { $destroy?: () => void };
};

export async function presentModal(
  host: ProfileHostPorts,
  options: Parameters<ProfileHostPorts['showModal']>[0],
  destroyScope = false
): Promise<unknown> {
  try {
    const modal = (await host.showModal(options)) as HostModal;
    modal.element?.modal?.();
    const result = await modal.close;
    if (destroyScope) {
      modal.scope?.$destroy?.();
    }
    return result;
  } catch {
    return undefined;
  }
}

function generalDialog(data: Record<string, unknown>) {
  return {
    templateUrl: GENERAL_DIALOG,
    controller: 'GeneralDialogController',
    controllerAs: 'vm',
    inputs: { data },
  };
}

async function showCancelError(host: ProfileHostPorts, message: unknown): Promise<void> {
  await presentModal(host, generalDialog({
    type: 'error_cancelling',
    title: 'WEB_ERROR_CANCELLING',
    message,
    buttonLabel: 'WEB_OK',
  }));
}

export async function cancelBookingRequest(
  host: ProfileHostPorts,
  userId: string,
  bookingId: string
): Promise<void> {
  try {
    const response = asRecord(await host.cancelBookingById(userId, bookingId));
    if (!response?.success) {
      await showCancelError(host, response?.message);
    }
  } catch (error) {
    const response = asRecord(error);
    await showCancelError(host, response?.message);
  }
}

export async function confirmCancelBooking(
  host: ProfileHostPorts,
  branch: ProfileRuntimeBranch | null,
  userId: string | undefined,
  booking: ProfileBooking
): Promise<void> {
  if (!userId || !booking._id) {
    return;
  }
  const copy = resolveCancelMessage({
    booking,
    branch,
    penalties: getBranchPenalties(branch),
    currencySymbol: host.currencySymbol(),
    nowUnix: Math.floor(Date.now() / 1000),
  });
  await presentModal(host, generalDialog({
    type: 'confirm_cancel_booking',
    title: t('WEB_CONFIRM_CANCEL_BOOKING_TITLE'),
    message: copy.message,
    buttonLabel: copy.confirmButtonLabel,
    buttonAction: copy.hasConfirmAction
      ? () => {
          void cancelBookingRequest(host, userId, booking._id as string);
        }
      : false,
  }));
}

export type SaveProfileChangesInput = {
  host: ProfileHostPorts;
  user: ProfileRuntimeUser;
  branch: ProfileRuntimeBranch | null;
  profile: ProfileIdentity;
  consents: unknown;
  image: File | null;
};

export async function saveProfileChanges(
  input: SaveProfileChangesInput
): Promise<{ ok: true; user: ProfileRuntimeUser | null } | { ok: false; errors: string[] }> {
  const { host, user, branch, profile, consents, image } = input;
  try {
    const response = asRecord(await host.modifyProfile(
      user._id as string,
      buildModifyProfilePayload(profile, host.getUserConsentObjectUpdated(consents), branch),
      image
    ));
    if (!response?.success) {
      host.triggerAnalytics({ name: 'PROFILE_UPDATE_ERROR' });
      const errors = Array.isArray(response?.errors)
        ? response.errors.map((error) => t(String(error)))
        : [];
      return { ok: false, errors };
    }
    host.triggerAnalytics({ name: 'PROFILE_UPDATE_SUCCESS' });
    return { ok: true, user: asUser(host.getCurrentUser()) };
  } catch {
    host.triggerAnalytics({ name: 'PROFILE_UPDATE_PASSWORD_ERROR' });
    return { ok: false, errors: [] };
  }
}

export async function goPayg(host: ProfileHostPorts, userId: string): Promise<boolean> {
  try {
    const response = asRecord(await host.buyMembershipPlan(userId, 'payg'));
    return response?.success === true;
  } catch {
    return false;
  }
}

export function showChangePassword(host: ProfileHostPorts): Promise<unknown> {
  return presentModal(host, {
    templateUrl: 'dialogs/change-password/change-password.html',
    controller: 'ChangePasswordController',
    controllerAs: 'vm',
    inputs: { isOnBoarding: false, userToOnBoard: null },
  });
}

export function showCreateLinkedAccount(host: ProfileHostPorts): Promise<unknown> {
  return presentModal(host, {
    templateUrl: 'dialogs/register-child-account/register-child-account.html',
    controller: 'RegisterChildAccountController',
    controllerAs: 'vm',
  });
}

export function showStudioWaiver(
  host: ProfileHostPorts,
  user: ProfileRuntimeUser | null
): Promise<unknown> {
  return presentModal(host, {
    templateUrl: WAIVERS,
    controller: 'WaiversController',
    controllerAs: 'vm',
    inputs: {
      data: {
        waiverType: 'STUDIOWAIVER',
        firstName: user?.first_name,
        lastName: user?.last_name,
        email: user?.email,
      },
      buttonAction: false,
    },
  });
}

export function showSmsWaiver(host: ProfileHostPorts): Promise<unknown> {
  return presentModal(host, {
    templateUrl: WAIVERS,
    controller: 'WaiversController',
    controllerAs: 'vm',
    inputs: { data: { waiverType: 'SMS' }, buttonAction: false },
  });
}

export function showRestrictedDialog(host: ProfileHostPorts): Promise<unknown> {
  host.triggerAnalytics({ name: 'TAP_ON_PROFILE_RESTRICTED_FIELD' });
  return presentModal(host, generalDialog({
    type: 'update_profile_restricted',
    title: t('WEB_UPDATE_PROFILE_RESTRICTED_TITLE'),
    message: t('WEB_UPDATE_PROFILE_RESTRICTED_MESSAGE'),
    buttonLabel: t('WEB_UPDATE_PROFILE_RESTRICTED_BUTTON'),
  }));
}

export function showFirstImageDialog(host: ProfileHostPorts): Promise<unknown> {
  return presentModal(host, generalDialog({
    type: 'update_profile_restricted',
    title: t('WEB_UPDATE_PROFILE_RESTRICTED_TITLE'),
    message: t('WEB_UPDATE_USER_IMAGE_FIRST_TIME_MESSAGE'),
    buttonLabel: t('WEB_UPDATE_USER_IMAGE_FIRST_TIME_BUTTON'),
    buttonAction: () => {
      document.getElementById('profileImageInput')?.click();
    },
  }));
}

export function showPaymentCollector(
  host: ProfileHostPorts,
  paymentMethod: string,
  onCardChanged: () => void
): Promise<unknown> {
  return presentModal(host, {
    templateUrl: 'dialogs/payment-collector/payment-collector.html',
    controller: 'PaymentCollectorController',
    controllerAs: 'vm',
    inputs: {
      paymentMethod,
      cartId: false,
      getCartStatus: null,
      origin: 'profile',
      hasNewPaymentMethodFeature: false,
    },
  }).then((result) => {
    const outcome = asRecord(result);
    if (outcome?.success === true && outcome.cardChanged === true) {
      onCardChanged();
    }
    return result;
  });
}

export async function confirmDeletePayment(
  host: ProfileHostPorts,
  user: ProfileRuntimeUser,
  method: 'card' | 'direct_debit',
  onDeleted: () => void
): Promise<void> {
  const message = method === 'card'
    ? 'WEB_PAYMENT_METHOD_CONFIRMATION_DELETE_CARD_MESSAGE'
    : 'WEB_PAYMENT_METHOD_CONFIRMATION_DELETE_MANDATE_MESSAGE';
  await presentModal(host, generalDialog({
    type: 'payg',
    message,
    buttonLabel: 'WEB_DELETE',
    action: () => {
      const request = method === 'card'
        ? host.deleteCardDetails(user._id as string)
        : host.deleteDirectDebitDetails(user._id as string, user.mandate?._id as string);
      void request.then(() => onDeleted());
    },
  }));
}

export function pauseMembership(
  host: ProfileHostPorts,
  membership: RuntimeMembership,
  onPaused: () => void
): Promise<unknown> {
  host.triggerAnalytics({ name: 'PAUSE_MEMBERSHIP_BUTTON_TAP' });
  return presentModal(host, {
    templateUrl: 'dialogs/pause-membership/pause-membership.html',
    controller: 'PauseMembershipController',
    controllerAs: 'vm',
    inputs: { data: { membership } },
  }).then((result) => {
    if (asRecord(result)?.pauseSuccess) {
      onPaused();
    }
    return result;
  });
}

function planFrequency(host: ProfileHostPorts, membership: RuntimeMembership): unknown {
  const duration = membership.membershipMetaData?.duration;
  if (!duration) {
    return undefined;
  }
  return host.translatePlanDuration(duration.value, duration.timeUnit);
}

function membershipWaiverData(
  host: ProfileHostPorts,
  user: ProfileRuntimeUser,
  membership: RuntimeMembership
): Record<string, unknown> {
  const meta = membership.membershipMetaData;
  return {
    waiverType: 'MEMBERSHIP',
    membershipName: meta?.membershipName,
    membershipPlan: meta?.planName,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    frequency: planFrequency(host, membership),
    joiningFee: meta?.upfrontFee,
    price: meta?.price,
  };
}

function showMembershipTerms(
  host: ProfileHostPorts,
  user: ProfileRuntimeUser,
  membership: RuntimeMembership
): Promise<unknown> {
  return presentModal(host, {
    templateUrl: WAIVERS,
    controller: 'WaiversController',
    controllerAs: 'vm',
    inputs: {
      data: membershipWaiverData(host, user, membership),
      buttonAction: false,
    },
  }, true);
}

function studioEmail(studio: Record<string, unknown> | null): string {
  const email = studio?.email;
  if (typeof email !== 'string') {
    return '';
  }
  return email;
}

async function showHomeBranchCancelBlock(host: ProfileHostPorts): Promise<void> {
  await presentModal(host, generalDialog({
    type: 'terms_and_conditions',
    title: t('WEB_FTC_CANCEL_MEMBERSHIP_HOME_BRANCH_TITLE'),
    message: t('WEB_FTC_CANCEL_MEMBERSHIP_HOME_BRANCH'),
    buttonLabel: t('WEB_FTC_CANCEL_MEMBERSHIP_HOME_BRANCH_BUTTON'),
    buttonAction: {},
  }));
}

async function showGroupCancelBlock(host: ProfileHostPorts, branchId: string): Promise<void> {
  const studio = asRecord(await host.getBranchById(branchId));
  await presentModal(host, generalDialog({
    type: 'terms_and_conditions',
    title: t('WEB_FTC_CANCEL_GROUP_MEMBERSHIP_TITLE'),
    message: t('WEB_FTC_CANCEL_GROUP_MEMBERSHIP').replace('{branchEmail}', studioEmail(studio)),
    buttonLabel: t('WEB_FTC_CANCEL_GROUP_MEMBERSHIP_BUTTON'),
    buttonAction: {},
  }));
}

function pauseDisabled(membership: RuntimeMembership): boolean {
  if (!membership.pause) {
    return false;
  }
  return !!membership.schedulePause;
}

function offerPauseInstead(membership: RuntimeMembership): boolean {
  if (!isPauseAllowed(membership)) {
    return false;
  }
  return !pauseDisabled(membership);
}

function cancelIsAllowed(enabled: boolean, membership: RuntimeMembership): boolean {
  if (!enabled) {
    return false;
  }
  return !membership.isCancellationRequested;
}

export type CancelMembershipInput = {
  host: ProfileHostPorts;
  user: ProfileRuntimeUser;
  branch: ProfileRuntimeBranch;
  membership: RuntimeMembership;
  onPause: () => void;
};

export async function cancelMembership(input: CancelMembershipInput): Promise<void> {
  const { host, user, branch, membership, onPause } = input;
  host.triggerAnalytics({ name: 'CANCEL_MEMBERSHIP_BUTTON_TAP' });
  host.triggerAnalytics({ category: 'profile-memberships', name: 'TRACK_CANCEL_FOR_MEMBERSHIPS' });
  if (branch._id !== user.branch_id) {
    await showHomeBranchCancelBlock(host);
    return;
  }
  if (membership.membershipMetaData?.isGroupMembership) {
    await showGroupCancelBlock(host, String(branch._id));
    return;
  }
  const studio = asRecord(await host.getBranchById(String(branch._id)));
  const result = await presentModal(host, {
    templateUrl: 'dialogs/cancel-membership/cancel-membership.html',
    controller: 'CancelMembershipController',
    controllerAs: 'vm',
    inputs: {
      data: {
        membershipName: membership.membershipMetaData?.membershipName,
        planName: membership.membershipMetaData?.planName,
        userMembershipId: membership.id,
        showMembershipPurchaseTermsDialog: () => {
          void showMembershipTerms(host, user, membership);
        },
        branchEmail: studioEmail(studio),
        showPauseInstead: offerPauseInstead(membership),
      },
    },
  });
  if (asRecord(result)?.openPause) {
    onPause();
  }
}

function openStudioMail(host: ProfileHostPorts, mailTo: string): void {
  host.triggerAnalytics({ category: 'profile-memberships', name: 'CONTACT_STUDIO' });
  window.open(mailTo, '_blank', 'noopener,noreferrer');
}

export type ManageMembershipInput = {
  host: ProfileHostPorts;
  user: ProfileRuntimeUser | null;
  membership: RuntimeMembership;
  isClickToCancelEnabled: boolean;
  mailTo: string;
  onCancel: () => void;
  onPause: () => void;
};

export function manageMembership(input: ManageMembershipInput): Promise<unknown> {
  const { host, user, membership } = input;
  host.triggerAnalytics({ name: 'MANAGE_MEMBERSHIP_BUTTON_TAP' });
  return presentModal(host, {
    templateUrl: 'dialogs/manage-membership/manage-membership.html',
    controller: 'ManageMembershipController',
    controllerAs: 'vm',
    inputs: {
      data: {
        membership,
        isPauseAllowed: isPauseAllowed(membership),
        isCancelAllowed: cancelIsAllowed(input.isClickToCancelEnabled, membership),
        isContactStudioEnabled: showContactStudioAboutMembership(user),
        onCancelMembership: input.onCancel,
        onContactStudioAboutMembership: () => openStudioMail(host, input.mailTo),
        onPauseMembership: input.onPause,
      },
    },
  });
}

function withDefaultConsent(member: Record<string, unknown>): Record<string, unknown> {
  if (!member.consent) {
    return {
      ...member,
      consent: { email: { active: false }, sms: { active: false } },
    };
  }
  return member;
}

export async function switchToChildAccount(
  host: ProfileHostPorts,
  parentId: string,
  childId: string,
  onDismiss: (result: ProfileCloseResult) => void
): Promise<void> {
  try {
    const token = asRecord(await host.getChildAccountToken(parentId, childId));
    if (!token || typeof token.token !== 'string') {
      return;
    }
    host.setCurrentToken(token.token, 'child');
    if (typeof token.refreshToken === 'string') {
      host.setCurrentToken(token.refreshToken, 'childRefreshToken');
    }
    host.setPaymentMethods();
    try {
      const member = asRecord(await host.getUserById(childId));
      if (!member) {
        return;
      }
      const next = withDefaultConsent(member);
      onDismiss({ success: false, redirect: false });
      host.setCurrentUser(next);
      host.setStoredUser(next);
      host.emitUserChange();
      host.triggerAnalytics({ name: 'SWITCH_TO_CHILD_ACCOUNT_SUCCESS' });
    } catch {
      host.triggerAnalytics({ name: 'SWITCH_TO_CHILD_ACCOUNT_ERROR' });
    } finally {
      host.emitLoading({ loader: 'getUser', loading: false });
    }
  } catch {
    host.triggerAnalytics({ name: 'SWITCH_TO_CHILD_ACCOUNT_TOKEN_ERROR' });
  }
}

export async function switchToParentAccount(
  host: ProfileHostPorts,
  parentId: string,
  onDismiss: (result: ProfileCloseResult) => void
): Promise<void> {
  host.removeChildToken();
  host.setPaymentMethods();
  try {
    const member = asRecord(await host.getUserById(parentId));
    if (!member) {
      return;
    }
    onDismiss({ success: false, redirect: false });
    host.setCurrentUser(member);
    host.setStoredUser(member);
    host.emitUserChange();
    host.triggerAnalytics({ name: 'SWITCH_TO_PARENT_ACCOUNT_SUCCESS' });
  } catch {
    host.triggerAnalytics({ name: 'SWITCH_TO_PARENT_ACCOUNT_ERROR' });
  } finally {
    host.emitLoading({ loader: 'getUser', loading: false });
  }
}

export function openAgreement(host: ProfileHostPorts, url: string, name: string): void {
  host.triggerAnalytics({ category: name, name: 'GO_TO_EAGREEMENTS' });
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function reloadMember(host: ProfileHostPorts, userId: string): Promise<ProfileRuntimeUser | null> {
  const member = asUser(await host.getUserById(userId));
  return member;
}
